import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  applyNodeChanges,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import StoryboardSidebar from './StoryboardSidebar';
import { Send, Settings, X, Trash2, Scissors } from 'lucide-react';
import TechnicalIcon from '../../../../assets/images/icons/Container.png';
import NonTechnicalIcon from '../../../../assets/images/icons/Symbol.png';
import {
  getActionMapping,
  setActionValue,
  getActionValue,
} from '../../utils/actionMapper';

// Action type definitions based on the Actions modal
const ACTION_TYPES = [
  { id: 'voiceOver', label: 'Voice Over', icon: '🎤' },
  { id: 'objects', label: 'Objects', icon: '📦' },
  { id: 'holoBooks', label: 'Holo Books', icon: '📖' },
  { id: 'highlights', label: 'Highlights', icon: '✨' },
  { id: 'player', label: 'Player', icon: '👤' },
  { id: 'baseItem', label: 'Base Item', icon: '📦' },
  { id: 'textMedia', label: 'Text Media', icon: '📝' },
  { id: 'videoMedia', label: 'Video Media', icon: '🎥' },
  { id: 'objAnimation', label: 'Obj Animation', icon: '🎬' },
  { id: 'uiUpdate', label: 'UI Update', icon: '💻' },
  { id: 'soundEffects', label: 'Sound Effects', icon: '🔊' },
  { id: 'timerDelay', label: 'Timer Delay', icon: '⏱️' },
  { id: 'animation', label: 'Animation', icon: '🎞️' },
  { id: 'stateTable', label: 'State Table', icon: '❄️' },
  { id: 'checklistUI', label: 'Checklist UI', icon: '✅' },
  { id: 'imageMedia', label: 'Image Media', icon: '🖼️' },
  { id: 'metaLayer', label: 'Meta Layer', icon: '📊' },
];

// Interaction gesture types matching the backend Unity enum
const INTERACTION_TYPES = [
  { value: 'poke',          label: 'Poke' },
  { value: 'grab',          label: 'Grab' },
  { value: 'twohandgrab',   label: 'Two Hand Grab' },
  { value: 'pinch',         label: 'Pinch' },
  { value: 'snapTurn',      label: 'Snap Turn' },
  { value: 'teleportation', label: 'Teleportation' },
  { value: 'climbing',      label: 'Climbing' },
  { value: 'pullingLever',  label: 'Pulling Lever' },
  { value: 'gravityFall',   label: 'Gravity Fall' },
  { value: 'placepoint',    label: 'Place Point' },
];

// Map backend enum value → display label
function getInteractionTypeDisplayLabel(value) {
  if (!value) return 'Poke';
  const match = INTERACTION_TYPES.find(
    (t) => t.value === value || t.value.toLowerCase() === value.toLowerCase()
  );
  return match ? match.label : value.charAt(0).toUpperCase() + value.slice(1);
}

// Start Node
const StartNode = ({ data }) => (
  <div className="bg-zinc-800 text-white px-4 py-3 rounded-md font-semibold text-sm min-w-[120px] text-center relative border border-zinc-700 shadow-xl">
    {data.label}
    <Handle
      type="source"
      position={Position.Right}
      className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
    />
  </div>
);

// On Awake Node
const OnAwakeNode = ({ data }) => (
  <div className="bg-zinc-800 text-white px-4 py-3 rounded-md font-medium text-sm min-w-[120px] text-center relative border border-zinc-700 shadow-xl">
    <Handle
      type="target"
      position={Position.Left}
      className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
    />
    <div className="flex items-center justify-center gap-1 mb-1">
      <div className="w-1 h-4 bg-white"></div>
      <div className="w-1 h-4 bg-white"></div>
    </div>
    {data.label}
    <Handle
      type="source"
      position={Position.Right}
      className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
    />
  </div>
);

// Generic Action Node Component
const ActionNode = ({ data, id }) => {
  const actionType = ACTION_TYPES.find((a) => a.id === data.actionType) || ACTION_TYPES[0];
  const onEdit = data.onEdit;
  const onDelete = data.onDelete;
  
  return (
    <div className="bg-zinc-800 text-white rounded-md text-xs w-[220px] relative border border-zinc-700 shadow-xl">
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
      />

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-zinc-750">
        <div className="flex items-center gap-2">
          <span className="text-base">{actionType.icon}</span>
          <span className="font-medium text-xs text-zinc-100">{actionType.label}</span>
        </div>
        <button className="text-zinc-500 hover:text-white text-lg leading-none font-light">
          ⋮
        </button>
      </div>

      {/* Content */}
      <div className="px-3 py-3 space-y-1.5 text-[11px] leading-relaxed bg-zinc-800">
        {data.query && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Query - </span>
            <span className="text-zinc-300">{data.query}</span>
          </div>
        )}
        {data.option && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Option - </span>
            <span className="text-zinc-300">{data.option}</span>
          </div>
        )}
        {data.text && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Text - </span>
            <span className="text-zinc-300">
              {data.text.length > 20 ? data.text.substring(0, 20) + '...' : data.text}
            </span>
          </div>
        )}
        {data.content && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Content - </span>
            <span className="text-zinc-300">
              {data.content.length > 20 ? data.content.substring(0, 20) + '...' : data.content}
            </span>
          </div>
        )}
        {data.waitForCompletion !== undefined && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Wait For Completion - </span>
            <span className="text-zinc-300">{data.waitForCompletion ? 'True' : 'False'}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2.5 bg-zinc-800 border-t border-zinc-700 flex gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit?.(data); }}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex-1 bg-zinc-700 hover:bg-zinc-650 text-zinc-200 py-1.5 rounded text-[11px] font-normal flex items-center justify-center gap-1.5 transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Edit
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete?.(data)}
            className="bg-red-600 hover:bg-red-700 text-white py-1.5 px-2 rounded text-[11px] font-normal flex items-center justify-center transition-colors"
            aria-label="Delete action"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
      />
    </div>
  );
};

// Interaction Node (Touch, Grab, Place Point, etc.)
const InteractionNode = ({ data, id }) => {
  const onEdit = data.onEdit;
  
  return (
    <div className="bg-zinc-800 text-white rounded-md text-xs w-[220px] relative border border-zinc-700 shadow-xl">
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
      />

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-zinc-750">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" />
            </svg>
          </div>
          <span className="font-medium text-xs text-zinc-100">{data.interactionType || 'Touch'}</span>
        </div>
        <button className="text-zinc-500 hover:text-white text-lg leading-none font-light">
          ⋮
        </button>
      </div>

      {/* Content */}
      <div className="px-3 py-3 space-y-1.5 text-[11px] leading-relaxed bg-zinc-800">
        <div className="text-zinc-300">
          <span className="text-zinc-500">Query - </span>
          <span className="text-zinc-300">{data.query || '—'}</span>
        </div>
        {data.grabbableName && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Grabbable - </span>
            <span className="text-zinc-300">{data.grabbableName}</span>
          </div>
        )}
        {data.disableGrabOnPlace !== undefined && (
          <div className="text-zinc-300">
            <span className="text-zinc-500">Disable Grab - </span>
            <span className="text-zinc-300">{data.disableGrabOnPlace ? 'Yes' : 'No'}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2.5 bg-zinc-800 border-t border-zinc-700">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit?.(data); }}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-full bg-zinc-700 hover:bg-zinc-650 text-zinc-200 py-1.5 rounded text-[11px] font-normal flex items-center justify-center gap-1.5 transition-colors"
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Edit
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-zinc-900 !border-2 !border-zinc-400 hover:!bg-zinc-700 transition-colors cursor-crosshair"
      />
    </div>
  );
};

const nodeTypes = {
  start: StartNode,
  onAwake: OnAwakeNode,
  action: ActionNode,
  interaction: InteractionNode,
};

// Custom dropdown to avoid native <select> styling issues
const GestureDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const selected = INTERACTION_TYPES.find((t) => t.value === value) || INTERACTION_TYPES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm flex items-center justify-between hover:bg-[#4a4a4a] transition-colors"
      >
        <span>{selected.label}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-10 w-full mt-1 bg-[#2a2a2e] border border-[#555555] rounded shadow-xl max-h-56 overflow-y-auto">
          {INTERACTION_TYPES.map(({ value: v, label }) => (
            <button
              key={v}
              type="button"
              onClick={() => { onChange(v); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                v === value
                  ? 'bg-[#3a3a3e] text-white font-medium'
                  : 'text-gray-300 hover:bg-[#35353a] hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Edit Interaction Modal
export const EditInteractionModal = ({ isOpen, onClose, interactionData, onSave, moment, onMomentUpdate }) => {
  const [formData, setFormData] = useState({
    query: '',
    option: 'poke',
    grabbableName: '',
    disableGrabOnPlace: false,
    disablePlacePointOnPlace: false,
    disableRigidbodyOnPlace: false,
  });

  // Normalize a raw backend value to a valid INTERACTION_TYPES value key
  const normalizeInteractionValue = (raw) => {
    if (!raw) return 'poke';
    const match = INTERACTION_TYPES.find(
      (t) => t.value === raw || t.value.toLowerCase() === raw.toLowerCase()
    );
    return match ? match.value : 'poke';
  };

  useEffect(() => {
    if (interactionData) {
      const raw = interactionData.option || interactionData.interactionType || 'poke';
      setFormData({
        query: interactionData.query || interactionData.touchObjectQuery || '',
        option: normalizeInteractionValue(raw),
        grabbableName: interactionData.grabbableName || '',
        disableGrabOnPlace: interactionData.disableGrabOnPlace || false,
        disablePlacePointOnPlace: interactionData.disablePlacePointOnPlace || false,
        disableRigidbodyOnPlace: interactionData.disableRigidbodyOnPlace || false,
      });
    } else if (moment?.content?.technical?.interaction) {
      const interaction = moment.content.technical.interaction;
      setFormData({
        query: interaction.touchObjectQuery || '',
        option: normalizeInteractionValue(interaction.interactionType || 'poke'),
        grabbableName: interaction.grabbableName || '',
        disableGrabOnPlace: interaction.disableGrabOnPlace || false,
        disablePlacePointOnPlace: interaction.disablePlacePointOnPlace || false,
        disableRigidbodyOnPlace: interaction.disableRigidbodyOnPlace || false,
      });
    }
  }, [interactionData, moment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (moment && onMomentUpdate) {
      // formData.option is already the backend enum value (e.g. 'poke', 'snapTurn')
      onMomentUpdate(moment.id, `content.technical.interaction.touchObjectQuery`, formData.query);
      onMomentUpdate(moment.id, `content.technical.interaction.interactionType`, formData.option);
      onMomentUpdate(moment.id, `content.nonTechnical.interaction.touchObjectQuery`, formData.query);
      onMomentUpdate(moment.id, `content.nonTechnical.interaction.interactionType`, formData.option);

      if (formData.option === 'placepoint') {
        onMomentUpdate(moment.id, `content.technical.interaction.grabbableName`, formData.grabbableName);
        onMomentUpdate(moment.id, `content.technical.interaction.disableGrabOnPlace`, formData.disableGrabOnPlace);
        onMomentUpdate(moment.id, `content.technical.interaction.disablePlacePointOnPlace`, formData.disablePlacePointOnPlace);
        onMomentUpdate(moment.id, `content.technical.interaction.disableRigidbodyOnPlace`, formData.disableRigidbodyOnPlace);
      }
    }
    onSave?.(formData);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999999]">
      <div className="bg-[#1E1F22] rounded-lg p-6 w-full max-w-md border border-[#444444]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Edit Interaction</h2>
            <p className="text-sm text-gray-400 mt-1">Interaction - {getInteractionTypeDisplayLabel(formData.option)}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">
            {formData.option === 'placepoint'
              ? 'Place your grabbable object into this place point to execute onRight event and move ahead in Story.'
              : `Configure the ${getInteractionTypeDisplayLabel(formData.option)} interaction for this moment.`}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Query / Object Name</label>
            <input
              type="text"
              value={formData.query}
              onChange={(e) => setFormData({ ...formData, query: e.target.value })}
              placeholder="<PlaceObject_Query>"
              className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Gesture / Interaction Type</label>
            <GestureDropdown
              value={formData.option}
              onChange={(v) => setFormData({ ...formData, option: v })}
            />
          </div>

          {formData.option === 'placepoint' && (
            <>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Grabbable Name</label>
                <input
                  type="text"
                  value={formData.grabbableName}
                  onChange={(e) => setFormData({ ...formData, grabbableName: e.target.value })}
                  placeholder="Grabbable Name"
                  className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-300">Disable Grab On Place</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, disableGrabOnPlace: !formData.disableGrabOnPlace })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.disableGrabOnPlace ? 'bg-red-600' : 'bg-gray-700'
                    }`}
                    aria-label="Toggle disable grab on place"
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.disableGrabOnPlace ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-300">Disable PlacePoint On Place</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        disablePlacePointOnPlace: !formData.disablePlacePointOnPlace,
                      })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.disablePlacePointOnPlace ? 'bg-red-600' : 'bg-gray-700'
                    }`}
                    aria-label="Toggle disable place point on place"
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.disablePlacePointOnPlace ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-300">Disable Rigidbody On Place</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        disableRigidbodyOnPlace: !formData.disableRigidbodyOnPlace,
                      })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.disableRigidbodyOnPlace ? 'bg-red-600' : 'bg-gray-700'
                    }`}
                    aria-label="Toggle disable rigidbody on place"
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.disableRigidbodyOnPlace ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </label>
              </div>
            </>
          )}

          <button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Submit
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

// Edit Action Modal - Uses actionMapper for all action types
const EditActionModal = ({ isOpen, onClose, actionData, moment, onMomentUpdate }) => {
  const [formData, setFormData] = useState({
    value: '',
    query: '',
    option: '',
    spawn: '',
    text: '',
    content: '',
    url: '',
    duration: 0,
    waitForCompletion: false,
  });

  useEffect(() => {
    if (actionData && moment) {
      const actionType = actionData.actionType;
      const isBeforeTouch = actionData.isBeforeTouch !== false;
      const mapping = getActionMapping(actionType, isBeforeTouch);
      const currentValue = getActionValue(moment, actionType, isBeforeTouch, true);

      if (mapping.isObject && typeof currentValue === 'object') {
        setFormData({
          query: currentValue.query || '',
          option: currentValue.option || '',
          spawn: currentValue.spawn || '',
          value: '',
          text: '',
          content: '',
          url: '',
          duration: 0,
          waitForCompletion: actionData.waitForCompletion || false,
        });
      } else if (mapping.isNumber) {
        setFormData({
          duration: currentValue || 0,
          value: '',
          query: '',
          option: '',
          spawn: '',
          text: '',
          content: '',
          url: '',
          waitForCompletion: false,
        });
      } else {
        const fieldValue = currentValue || '';
        setFormData({
          [mapping.field]: fieldValue,
          value: fieldValue,
          query: '',
          option: '',
          spawn: '',
          text: '',
          content: '',
          url: '',
          duration: 0,
          waitForCompletion: actionData.waitForCompletion || false,
        });
      }
    }
  }, [actionData, moment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (moment && onMomentUpdate && actionData) {
      const actionType = actionData.actionType;
      const isBeforeTouch = actionData.isBeforeTouch !== false;
      const mapping = getActionMapping(actionType, isBeforeTouch);

      if (mapping.isObject) {
        setActionValue(
          moment.id,
          onMomentUpdate,
          actionType,
          {
            query: formData.query,
            option: formData.option,
            spawn: formData.spawn,
          },
          isBeforeTouch,
          true
        );
      } else if (mapping.isNumber) {
        setActionValue(
          moment.id,
          onMomentUpdate,
          actionType,
          formData.duration,
          isBeforeTouch,
          true
        );
      } else {
        const value = formData[mapping.field] || formData.value || '';
        setActionValue(moment.id, onMomentUpdate, actionType, value, isBeforeTouch, true);
      }
    }
    onClose();
  };

  if (!isOpen || !actionData) return null;

  const actionType = ACTION_TYPES.find((a) => a.id === actionData.actionType);
  const mapping = getActionMapping(actionData.actionType, actionData.isBeforeTouch !== false);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999999]">
      <div className="bg-[#1E1F22] rounded-lg p-6 w-full max-w-md border border-[#444444] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Edit Action</h2>
            <p className="text-sm text-gray-400 mt-1">{actionType?.label || 'Action'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mapping.isObject ? (
            <>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Query</label>
                <input
                  type="text"
                  value={formData.query}
                  onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                  placeholder="Object query"
                  className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Option</label>
                <select
                  value={formData.option}
                  onChange={(e) => setFormData({ ...formData, option: e.target.value })}
                  className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
                >
                  <option value="Highlight">Highlight</option>
                  <option value="Enable">Enable</option>
                  <option value="Disable">Disable</option>
                  <option value="Spawn">Spawn</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Spawn (Optional)</label>
                <input
                  type="text"
                  value={formData.spawn}
                  onChange={(e) => setFormData({ ...formData, spawn: e.target.value })}
                  placeholder="Spawn configuration"
                  className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
                />
              </div>
            </>
          ) : mapping.isNumber ? (
            <div>
              <label className="block text-sm text-gray-300 mb-1">{mapping.label}</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseFloat(e.target.value) || 0 })}
                placeholder="Duration in seconds"
                min="0"
                step="0.1"
                className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
              />
            </div>
          ) : mapping.field === 'text' || mapping.field === 'content' ? (
            <div>
              <label className="block text-sm text-gray-300 mb-1">{mapping.label}</label>
              <textarea
                value={formData[mapping.field] || formData.value}
                onChange={(e) => setFormData({ ...formData, [mapping.field]: e.target.value, value: e.target.value })}
                placeholder={`Enter ${mapping.label.toLowerCase()}`}
                rows={4}
                className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
              />
            </div>
          ) : mapping.field === 'url' ? (
            <div>
              <label className="block text-sm text-gray-300 mb-1">{mapping.label}</label>
              <input
                type="url"
                value={formData.url || formData.value}
                onChange={(e) => setFormData({ ...formData, url: e.target.value, value: e.target.value })}
                placeholder="Enter URL"
                className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-300 mb-1">{mapping.label}</label>
              <input
                type="text"
                value={formData[mapping.field] || formData.value}
                onChange={(e) => setFormData({ ...formData, [mapping.field]: e.target.value, value: e.target.value })}
                placeholder={`Enter ${mapping.label.toLowerCase()}`}
                className="w-full bg-[#404040] border border-[#555555] text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-500"
              />
            </div>
          )}

          {(actionData.actionType === 'voiceOver' || actionData.actionType === 'textMedia') && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.waitForCompletion}
                onChange={(e) => setFormData({ ...formData, waitForCompletion: e.target.checked })}
                className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-300">Wait For Completion</span>
            </label>
          )}

          <button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Submit
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

// Build workflow nodes dynamically from moment data
function buildWorkflowNodes(moment, savedPositions = null, callbacks = {}) {
  if (!moment) return { nodes: [], edges: [] };

  const technical = moment.content?.technical || {};
  const content = technical;

  const beforeTouch = content.beforeTouch || {};
  const afterTouch = content.afterTouch || {};
  const interaction = content.interaction || {};

  const nodes = [];
  const edges = [];
  let nodeIdCounter = 0;

  const getNextNodeId = (prefix) => `${prefix}_${nodeIdCounter++}`;
  const getNodePosition = (nodeId, defaultX, defaultY) => {
    if (savedPositions?.[nodeId]) {
      return savedPositions[nodeId];
    }
    return { x: defaultX, y: defaultY };
  };

  let xPos = 200;
  const xSpacing = 280;
  const centerY = 300;
  let currentY = centerY;

  // Start node
  const startId = 'start';
  nodes.push({
    id: startId,
    type: 'start',
    position: getNodePosition(startId, xPos, currentY),
    data: { label: 'Start' },
    draggable: true,
  });

  xPos += xSpacing;

  // On Awake node
  const onAwakeId = getNextNodeId('onAwake');
  nodes.push({
    id: onAwakeId,
    type: 'onAwake',
    position: getNodePosition(onAwakeId, xPos, currentY),
    data: { label: 'On Awake' },
    draggable: true,
  });

  edges.push({
    id: `${startId}-to-${onAwakeId}`,
    source: startId,
    target: onAwakeId,
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 2 },
  });

  xPos += xSpacing;
  let lastNodeId = onAwakeId;
  const beforeTouchNodes = [];

  // Descriptor table: maps every action type to how its value is stored and what data to put in the node.
  // beforeKey / afterKey are the sub-keys inside beforeTouch / afterTouch objects.
  // hasValue(v) decides if the value is non-empty enough to show a node.
  // buildData(v, isBeforeTouch) returns the node data object.
  const ACTION_NODE_DEFS = [
    {
      id: 'voiceOver', beforeKey: 'voiceOver', afterKey: 'voiceOver',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'voiceOver', query: 'VOPlayer', option: 'Play', text: v, waitForCompletion: true, isBeforeTouch: isBT }),
    },
    {
      id: 'textMedia', beforeKey: 'displayText', afterKey: 'displayText',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'textMedia', content: v, waitForCompletion: true, isBeforeTouch: isBT }),
    },
    {
      id: 'objects', beforeKey: 'objects', afterKey: 'objects',
      hasValue: v => !!(v?.query),
      buildData: (v, isBT) => ({ actionType: 'objects', query: v.query, option: v.option || 'Highlight', spawn: v.spawn || '', isBeforeTouch: isBT }),
    },
    {
      id: 'soundEffects', beforeKey: null, afterKey: 'sfx',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'soundEffects', query: 'SFXPlayer', option: 'Play', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'metaLayer', beforeKey: 'metaLayer', afterKey: 'metaLayer',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'metaLayer', query: v, option: 'Edit', isBeforeTouch: isBT }),
    },
    {
      id: 'holoBooks', beforeKey: 'holoBooks', afterKey: 'holoBooks',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'holoBooks', content: v, isBeforeTouch: isBT }),
    },
    {
      id: 'highlights', beforeKey: 'highlights', afterKey: 'highlights',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'highlights', query: v, isBeforeTouch: isBT }),
    },
    {
      id: 'player', beforeKey: 'player', afterKey: 'player',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'player', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'baseItem', beforeKey: 'baseItem', afterKey: 'baseItem',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'baseItem', query: v, isBeforeTouch: isBT }),
    },
    {
      id: 'objAnimation', beforeKey: 'objAnimation', afterKey: 'objAnimation',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'objAnimation', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'uiUpdate', beforeKey: 'uiUpdate', afterKey: 'uiUpdate',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'uiUpdate', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'timerDelay', beforeKey: 'timerDelay', afterKey: 'timerDelay',
      hasValue: v => v != null && v !== 0,
      buildData: (v, isBT) => ({ actionType: 'timerDelay', duration: v, isBeforeTouch: isBT }),
    },
    {
      id: 'animation', beforeKey: 'animation', afterKey: 'animation',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'animation', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'stateTable', beforeKey: 'stateTable', afterKey: 'stateTable',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'stateTable', text: v, isBeforeTouch: isBT }),
    },
    {
      id: 'checklistUI', beforeKey: 'checklistUI', afterKey: 'checklistUI',
      hasValue: v => Array.isArray(v) ? v.length > 0 : !!v,
      buildData: (v, isBT) => ({ actionType: 'checklistUI', content: Array.isArray(v) ? v.join(', ') : v, isBeforeTouch: isBT }),
    },
    {
      id: 'imageMedia', beforeKey: 'image', afterKey: 'image',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'imageMedia', url: v, isBeforeTouch: isBT }),
    },
    {
      id: 'videoMedia', beforeKey: 'video', afterKey: 'video',
      hasValue: v => !!v,
      buildData: (v, isBT) => ({ actionType: 'videoMedia', url: v, isBeforeTouch: isBT }),
    },
  ];

  const edgeStyle = { stroke: '#10b981', strokeWidth: 2 };

  // Helper: push an action node and its incoming edge
  const addActionNode = (nodeId, nodeData, posX, posY, sourceId, toBTList) => {
    nodes.push({
      id: nodeId, type: 'action',
      position: getNodePosition(nodeId, posX, posY),
      data: { ...nodeData, onEdit: callbacks.onEditAction, onDelete: callbacks.onDeleteAction },
      draggable: true,
    });
    edges.push({ id: `${sourceId}-to-${nodeId}`, source: sourceId, target: nodeId, animated: true, style: edgeStyle });
    if (toBTList) toBTList.push(nodeId);
  };

  // Before Touch nodes — all action types with a non-empty beforeKey value
  let btIdx = 0;
  for (const def of ACTION_NODE_DEFS) {
    if (!def.beforeKey) continue;
    const value = beforeTouch[def.beforeKey];
    if (!def.hasValue(value)) continue;
    const nodeId = getNextNodeId(def.id + 'BT');
    const yOffset = (btIdx - Math.floor(ACTION_NODE_DEFS.length / 2)) * 90;
    addActionNode(nodeId, def.buildData(value, true), xPos, currentY + yOffset, lastNodeId, beforeTouchNodes);
    btIdx++;
  }

  // Interaction Node
  if (interaction.touchObjectQuery) {
    xPos += xSpacing;
    const interactionId = getNextNodeId('interaction');
    nodes.push({
      id: interactionId, type: 'interaction',
      position: getNodePosition(interactionId, xPos, currentY),
      data: {
        interactionType: getInteractionTypeDisplayLabel(interaction.interactionType || 'touch'),
        query: interaction.touchObjectQuery,
        grabbableName: interaction.grabbableName || '',
        disableGrabOnPlace: interaction.disableGrabOnPlace || false,
        disablePlacePointOnPlace: interaction.disablePlacePointOnPlace || false,
        disableRigidbodyOnPlace: interaction.disableRigidbodyOnPlace || false,
        onEdit: callbacks.onEditInteraction,
      },
      draggable: true,
    });

    if (beforeTouchNodes.length > 0) {
      beforeTouchNodes.forEach(nid => edges.push({ id: `${nid}-to-${interactionId}`, source: nid, target: interactionId, animated: true, style: edgeStyle }));
    } else {
      edges.push({ id: `${lastNodeId}-to-${interactionId}`, source: lastNodeId, target: interactionId, animated: true, style: edgeStyle });
    }
    lastNodeId = interactionId;
    xPos += xSpacing;
  }

  // After Touch nodes — all action types with a non-empty afterKey value
  let atIdx = 0;
  for (const def of ACTION_NODE_DEFS) {
    if (!def.afterKey) continue;
    const value = afterTouch[def.afterKey];
    if (!def.hasValue(value)) continue;
    const nodeId = getNextNodeId(def.id + 'AT');
    const yOffset = (atIdx - Math.floor(ACTION_NODE_DEFS.length / 2)) * 90;
    addActionNode(nodeId, def.buildData(value, false), xPos, currentY + yOffset, lastNodeId, null);
    atIdx++;
  }

  return { nodes, edges };
}

export default function NodeWorkflow({
  chapters,
  searchQuery,
  selectedMoment,
  onSearchChange,
  onMomentSelect,
  isTechnicalMode,
  moment,
  onPublish,
  onModeChange,
  onMomentUpdate,
  onSaveInteraction,
}) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [editingAction, setEditingAction] = useState(null);
  const [edgeMenu, setEdgeMenu] = useState(null); // { id, x, y }


  // IMPORTANT:
  // Some parents update nested moment content without changing the moment object reference.
  // ReactFlow nodes are derived from moment.content.technical, so we need a stable signature
  // to trigger rebuilds when those nested values change (e.g. gesture / interactionType).
  const workflowSignature = useMemo(() => {
    if (!moment) return '';
    const technical = moment.content?.technical || {};
    // Only include fields that affect rendered nodes/labels to keep this reasonably cheap.
    return JSON.stringify({
      interaction: technical.interaction || {},
      beforeTouch: technical.beforeTouch || {},
      afterTouch: technical.afterTouch || {},
    });
  }, [moment?.id, moment?.content?.technical]);

  // Load saved positions and build nodes
  useEffect(() => {
    if (!moment) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const savedPositions = JSON.parse(
      localStorage.getItem(`nodePositions_${moment.id}`) || '{}'
    );

    const { nodes: builtNodes, edges: builtEdges } = buildWorkflowNodes(moment, savedPositions, {
      onEditAction: (data) => setEditingAction(data),
      onEditInteraction: (data) => setEditingInteraction(data),
      onDeleteAction: (data) => {
        if (!moment || !onMomentUpdate) return;
        const actionType = data.actionType;
        const isBeforeTouch = data.isBeforeTouch !== false;
        const mapping = getActionMapping(actionType, isBeforeTouch);
        if (mapping.isObject) {
          setActionValue(moment.id, onMomentUpdate, actionType, { query: '', option: '', spawn: '' }, isBeforeTouch, true);
        } else {
          setActionValue(moment.id, onMomentUpdate, actionType, mapping.defaultValue, isBeforeTouch, true);
        }
      },
    });

    setNodes(builtNodes);
    setEdges(builtEdges);
  }, [moment?.id, workflowSignature]);

  // Handle node changes (dragging, etc.)
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      
      // Save positions
      if (moment?.id) {
        const positions = {};
        updatedNodes.forEach((node) => {
          positions[node.id] = node.position;
        });
        localStorage.setItem(`nodePositions_${moment.id}`, JSON.stringify(positions));
      }

      return updatedNodes;
    });
  }, [moment?.id]);

  // Handle edge connections
  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#10b981', strokeWidth: 2 } }, eds));
  }, []);

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setEdgeMenu({ id: edge.id, x: event.clientX, y: event.clientY });
  }, []);

  const onPaneClick = useCallback(() => {
    setEdgeMenu(null);
  }, []);


  const handleSaveInteraction = (formData) => {
    // formData.option is already the backend enum value (e.g. 'poke', 'snapTurn')
    setTimeout(() => {
      onSaveInteraction?.();
    }, 0);
  };

  if (!moment) {
    return (
      <div className="w-full h-[600px] bg-zinc-900 rounded-lg flex items-center justify-center text-gray-400 border border-zinc-800">
        Select a moment to view the technical workflow
      </div>
    );
  }

  return (
    <>
      {/* Top-right controls */}
      <div className="fixed top-6 right-6 z-[100099999] pointer-events-auto">
        <div className="flex items-center gap-4 bg-[#1E1F22] rounded-md p-2 border-[#444444] border-2">
          <div className="flex bg-[#444444] px-2 py-1 rounded-md gap-2">
            <button
              onClick={() => onModeChange?.(false)}
              className={`px-4 py-2 flex items-center gap-2 rounded text-sm font-medium transition-colors ${
                isTechnicalMode
                  ? ' text-gray-300 hover:bg-zinc-700'
                  : 'border-red-600 border-2 text-white'
              }`}
            >
              <img src={NonTechnicalIcon} className="w-[20px] h-[20px]" alt="Non-Technical" />
              {' '}Non-Technical
            </button>
            <button
              onClick={() => onModeChange?.(true)}
              className={`px-4 py-2 flex items-center justify-center gap-2 rounded text-sm font-medium transition-colors ${
                isTechnicalMode
                  ? 'border-red-600 border-2 text-white'
                  : 'border-zinc-800 border-2 text-gray-300 hover:bg-zinc-700'
              }`}
            >
              <img src={TechnicalIcon} className="w-[20px] h-[20px]" alt="Technical" />
              {' '}Technical
            </button>
          </div>
          <div className="flex gap-2">
            {onPublish && (
              <button
                onClick={onPublish}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            )}
            <button className="bg-zinc-800 hover:bg-zinc-700 text-gray-300 p-2 rounded transition-colors">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="fixed left-6 top-1/2 -translate-y-1/2 z-[10008889898] w-full max-w-[380px] pointer-events-auto flex items-center">
        <StoryboardSidebar
          chapters={chapters}
          searchQuery={searchQuery}
          selectedMoment={selectedMoment}
          onSearchChange={onSearchChange}
          onMomentSelect={onMomentSelect}
          isTechnicalMode={isTechnicalMode}
          noscrollbar={true}
        />
      </div>

      {/* ReactFlow container */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100088898] w-[100vw] h-[100vh] bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}
          zoomOnScroll={true}
          panOnDrag={true}
          zoomOnPinch={true}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#3f3f46" gap={20} size={1} />
          <Controls className="bg-zinc-800 border border-zinc-700 rounded-lg" />
        </ReactFlow>
      </div>

      {/* Modals */}
      <EditInteractionModal
        isOpen={!!editingInteraction}
        onClose={() => setEditingInteraction(null)}
        interactionData={editingInteraction}
        onSave={handleSaveInteraction}
        moment={moment}
        onMomentUpdate={onMomentUpdate}
      />

      <EditActionModal
        isOpen={!!editingAction}
        onClose={() => setEditingAction(null)}
        actionData={editingAction}
        moment={moment}
        onMomentUpdate={onMomentUpdate}
      />


      {/* Edge detach popup */}
      {edgeMenu && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-[999999998]" onClick={() => setEdgeMenu(null)} />
          <div
            className="fixed z-[999999999] bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl overflow-hidden"
            style={{ left: edgeMenu.x, top: edgeMenu.y, transform: 'translate(-50%, calc(-100% - 10px))' }}
          >
            <button
              onClick={() => {
                setEdges(eds => eds.filter(e => e.id !== edgeMenu.id));
                setEdgeMenu(null);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-xs text-red-400 hover:text-white hover:bg-red-600 w-full transition-colors whitespace-nowrap"
            >
              <Scissors className="w-3.5 h-3.5" />
              Detach
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

NodeWorkflow.propTypes = {
  moment: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.object,
  }),
  chapters: PropTypes.array,
  searchQuery: PropTypes.string,
  selectedMoment: PropTypes.string,
  onSearchChange: PropTypes.func,
  onMomentSelect: PropTypes.func,
  isTechnicalMode: PropTypes.bool,
  onPublish: PropTypes.func,
  onModeChange: PropTypes.func,
  onMomentUpdate: PropTypes.func,
};
